import { CartOrder, ValidateTransactionResponse } from "@ac-expo/api-types";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { OrderStatus } from "@prisma/client";

type CartContextType = {
  isOpen: boolean;
  cartOrder: CartOrder | null;
  toggleCart: (value?: boolean) => void;
  updateCartOrder: (order: ValidateTransactionResponse | null) => void;
};

const CartContext = createContext<CartContextType>({} as CartContextType);

type Props = {
  children: ReactNode;
};

function CartProvider({ children }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [cartOrder, setCartOrder] = useState<CartOrder | null>(null);

  const toggleCart = useCallback((value?: boolean) => {
    if (typeof value === "undefined") {
      setIsOpen((prev) => !prev);
    } else {
      setIsOpen(value);
    }
  }, []);

  const updateCartOrder = useCallback(
    (order: ValidateTransactionResponse | null) => {
      if (!order) {
        setCartOrder(order);
        return;
      }
      setCartOrder({
        ...order,
        status:
          order.status === OrderStatus.processing
            ? OrderStatus.success
            : order.status,
      });
    },
    []
  );

  const memoizedValue = useMemo(
    () => ({ isOpen, toggleCart, cartOrder, updateCartOrder }),
    [isOpen, cartOrder, toggleCart, updateCartOrder]
  );

  return (
    <CartContext.Provider value={memoizedValue}>
      {children}
    </CartContext.Provider>
  );
}

const useCartContext = () => useContext(CartContext);

export { useCartContext, CartProvider };
